import { Component, OnInit, ViewChild, TemplateRef, AfterViewInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableComponent, TableColumn, PaginationConfig, TabModalComponent, TabConfig } from '../../shared/components/primeng';
import { ActionDropdownTriggerDirective } from '../../shared/directives/action-dropdown-trigger.directive';
import { DateRange } from '../../shared/components/primeng/date-range-picker/date-range-picker.component';
import { FilterPanelComponent, FilterField } from '../../shared/components/filter-panel/filter-panel.component';
import { ActivitiesApiService, AuthApiService } from '@angular-workspace/services';
import { FormatService } from '../../shared/services/format.service';
import { FilterEnumsService } from '../../shared/services/filter-enums.service';
import { KeycloakService } from 'keycloak-angular';

export interface ActivityData {
  id?: string;
  assetName: string;
  assetType: string;
  assetTypeApiValue?: string; // Store original API value (e.g., "adex:DataBank") for filtering
  activity: string;
  accessedAt: string;
  userName?: string;
  userId?: string; // User ID for API calls
  role?: string; // For Compute tab: role from API (e.g. "consumer")
}

export interface ActivityFilters {
  type: string[];
  activity: string[];
  dateRange: DateRange | null;
  user?: string;
}

@Component({
  selector: 'app-admin-user-activities',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    TabModalComponent,
    FilterPanelComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './user-activities.component.html',
  styleUrl: './user-activities.component.scss',
})
export class AdminUserActivitiesComponent implements OnInit, AfterViewInit {
  filterFields: FilterField[] = [];
  /** Fields passed to the filter panel (Asset Type omitted on Compute tab). */
  displayFilterFields: FilterField[] = [];

  @ViewChild('activityCell', { static: false }) activityCellTemplate!: TemplateRef<any>;
  @ViewChild('actionCell', { static: false }) actionCellTemplate!: TemplateRef<any>;
  @ViewChild('userInfoTabContent', { static: false }) userInfoTabContentTemplate!: TemplateRef<any>;
  @ViewChild('activityInfoTabContent', { static: false }) activityInfoTabContentTemplate!: TemplateRef<any>;
  
  openDropdownId: string | null = null;
  isModalVisible = false;
  selectedActivity: ActivityData | null = null;
  userDetails: any = null; // Store user details from API
  modalTabs: TabConfig[] = [];

  constructor(
    private activitiesApiService: ActivitiesApiService,
    public formatService: FormatService,
    private filterEnumsService: FilterEnumsService,
    private authApiService: AuthApiService,
    private keycloak: KeycloakService
  ) {
    // Initialize filter fields with enum values
    this.filterFields = [
      {
        key: 'type',
        label: 'Asset Type',
        type: 'multiselect',
        placeholder: 'Choose Type',
        options: this.filterEnumsService.getUserActivitiesAssetTypeOptions(),
        value: []
      },
      {
        key: 'activity',
        label: 'Activity',
        type: 'multiselect',
        placeholder: 'Choose Activities',
        options: this.filterEnumsService.getActivityOptions(),
        value: []
      },
      {
        key: 'dateRange',
        label: 'Date Range',
        type: 'dateRange',
        placeholder: 'Choose Date Range',
        value: null
      }
    ];
    this.syncDisplayFilterFields();
  }

  /** Update `displayFilterFields` when the active tab changes. */
  private syncDisplayFilterFields(): void {
    this.displayFilterFields =
      this.activeLogType === 'COMPUTE'
        ? this.filterFields.filter((f) => f.key !== 'type')
        : this.filterFields;
  }

  filters: ActivityFilters = {
    type: [],
    activity: [],
    dateRange: null
  };

  typeOptions: string[] = [];
  activityOptions: string[] = [];
  assetTypeDisplayToApiMap = new Map<string, string>(); // Map display names to API values

  /** Compute tab activity filter options */
  private readonly computeActivityOptions = [
    'Start',
    'Stop',
    'Create',
    'Delete',
    'Get compute requests',
  ];

  /** User action tab: filter + default `action` query params */
  private readonly userActionActivityOptions = ['Bookmark', 'Unbookmark', 'Like', 'Dislike'];

  /**
   * When no Activity filter is selected, send each tab’s options as repeated `action` query params
   * (same pattern as admin auditing: logType=COMPUTE → action=Create&action=Delete&…).
   */
  private getDefaultActionsForLogType(logType: 'ASSET' | 'COMPUTE' | 'USER_ACTION'): string[] {
    if (logType === 'COMPUTE') {
      return this.computeActivityOptions.map((a) => this.formatService.toPascalCase(a));
    }
    if (logType === 'USER_ACTION') {
      return this.userActionActivityOptions.map((a) => this.formatService.toPascalCase(a));
    }
    return this.filterEnumsService.getActivityOptions().map((a) => this.formatService.toPascalCase(a));
  }

  /** Asset / User Actions tab columns */
  private assetColumns: TableColumn[] = [
    { key: 'assetName', label: 'Asset Name', sortable: true, width: '25%' },
    { key: 'assetType', label: 'Asset Type', sortable: false, width: '20%' },
    { key: 'activity', label: 'Activity', sortable: true, width: '20%' },
    { key: 'accessedAt', label: 'Date', sortable: true, width: '20%', align: 'left' },
    { key: 'action', label: 'Action', sortable: false, width: '15%', align: 'left', fixed: 'right' }
  ];

  /** Compute tab columns: Username, Role, Created At, Action (from response – no dropdown) */
  private computeColumns: TableColumn[] = [
    { key: 'userName', label: 'Username', sortable: true, width: '35%' },
    { key: 'role', label: 'Role', sortable: false, width: '25%' },
    { key: 'accessedAt', label: 'Created At', sortable: true, width: '20%', align: 'left' },
    { key: 'activity', label: 'Action', sortable: true, width: '20%', align: 'left' }
  ];

  get columns(): TableColumn[] {
    return this.activeLogType === 'COMPUTE' ? this.computeColumns : this.assetColumns;
  }

  allActivitiesData: ActivityData[] = [];
  activitiesData: ActivityData[] = [];

  pagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50, 100],
    serverSide: true
  };


  loading = false;
  emptyMessage = 'No User Activities Found';

  /** Current sort state for server-side sorting */
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' = 'asc';

  /** Active log type tab: ASSET | COMPUTE | USER_ACTION (passed as query param to API) */
  activeLogType: 'ASSET' | 'COMPUTE' | 'USER_ACTION' = 'ASSET';

  async ngOnInit(): Promise<void> {
    await this.waitForKeycloakReady();
    this.loadActivities();
  }

  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      if (keycloakInstance?.authenticated && keycloakInstance?.token) return;
      const maxWaitTime = 5000, checkInterval = 100, startTime = Date.now();
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) return;
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
    }
  }

  setLogType(logType: 'ASSET' | 'COMPUTE' | 'USER_ACTION'): void {
    if (this.activeLogType === logType) return;
    this.closeActionDropdown();
    this.activeLogType = logType;
    this.pagination = { ...this.pagination, currentPage: 1 };
    if (logType === 'COMPUTE') {
      this.filters.type = [];
      const typeField = this.filterFields.find((f) => f.key === 'type');
      if (typeField) {
        typeField.value = [];
      }
    }
    this.syncDisplayFilterFields();
    // Update Activity filter options per tab so dropdown shows correct list immediately
    const activityField = this.filterFields.find(f => f.key === 'activity');
    if (activityField) {
      if (logType === 'COMPUTE') {
        activityField.options = [...this.computeActivityOptions];
      } else if (logType === 'USER_ACTION') {
        activityField.options = [...this.userActionActivityOptions];
      } else {
        activityField.options = this.filterEnumsService.getActivityOptions();
      }
    }
    this.loadActivities();
  }

  async loadActivities(): Promise<void> {
    this.loading = true;
    try {
      // Convert display asset types to API format using FilterEnumsService (only when user selects type filter)
      // When no type filter is selected, do not send assetType params
      let assetTypes: string[] | undefined;
      if (this.filters.type && this.filters.type.length > 0) {
        assetTypes = this.filters.type.map(type => this.filterEnumsService.mapAssetTypeToApi(type));
      }

      // Convert activity filter values to Pascal case for API (action param)
      let operations =
        this.filters.activity && this.filters.activity.length > 0
          ? this.filters.activity.map((activity) => this.formatService.toPascalCase(activity))
          : undefined;

      if (!operations || operations.length === 0) {
        operations = this.getDefaultActionsForLogType(this.activeLogType);
      }

      // Format date range for API (ISO format: YYYY-MM-DDTHH:mm:ss)
      const formattedDateRange = this.formatService.formatDateRangeForApi(this.filters.dateRange);
      const startDate = formattedDateRange?.startDate;
      const endDate = formattedDateRange?.endDate;

      // Build sort query param (fieldName:asc or fieldName:desc)
      // Map UI column keys to API field names
      const columnToApiField: Record<string, string> = this.activeLogType === 'COMPUTE'
        ? { activity: 'action', accessedAt: 'createdAt', userName: 'userName', role: 'role' }
        : {
            assetName: 'assetName',
            activity: 'action',
            accessedAt: 'createdAt',
          };
      const apiSortField = this.sortColumn
        ? (columnToApiField[this.sortColumn] ?? this.sortColumn)
        : null;
      const sortParam = apiSortField
        ? `${apiSortField}:${this.sortDirection}`
        : undefined;

      const response = await this.activitiesApiService.getAllPlatformUserActivities(
        this.pagination.currentPage,
        this.pagination.pageSize,
        assetTypes,
        operations,
        startDate,
        endDate,
        sortParam,
        this.activeLogType
      );
      
      let mappedData: ActivityData[] = [];
      
      // Handle API response structure: { result: [...], paginationInfo: {...} }
      let originalTotalCount: number | undefined;
      if (response && response['paginationInfo'] && response['paginationInfo']['totalCount'] !== undefined) {
        originalTotalCount = response['paginationInfo']['totalCount'];
      }
      
      const mapComputeItem = (item: any): ActivityData => ({
        id: item.id || JSON.stringify(item),
        userName: item.userName || item.user_name || item.userId || 'Unknown User',
        userId: item.userId || item.user_id || item.id,
        role: item.role ? (item.role.charAt(0).toUpperCase() + item.role.slice(1).toLowerCase()) : '',
        activity: this.toPascalCase(item.action || item.activity || ''),
        accessedAt: this.formatService.formatDate(item.createdAt || item.accessedAt || item.accessed_at || item.timestamp || ''),
        assetName: '',
        assetType: '',
        assetTypeApiValue: undefined
      });

      const mapAssetItem = (item: any): ActivityData => {
        const entityTypeApiValue = item.entityType || item.assetType || item.asset_type || item.type || '';
        const normalizedApiValue = this.normalizeApiEntityType(entityTypeApiValue);
        return {
          id: item.id || JSON.stringify(item),
          userName: item.userName || item.user_name || item.userId || 'Unknown User',
          userId: item.userId || item.user_id || item.id,
          assetName: item.entityName || item.assetName || item.asset_name || item.name || '',
          assetType: this.formatService.formatAssetType(this.toPascalCase(normalizedApiValue)),
          assetTypeApiValue: normalizedApiValue,
          activity: this.toPascalCase(item.action || item.activity || ''),
          accessedAt: this.formatService.formatDate(item.createdAt || item.accessedAt || item.accessed_at || item.timestamp || '')
        };
      };

      const mapItem = (item: any): ActivityData =>
        this.activeLogType === 'COMPUTE' ? mapComputeItem(item) : mapAssetItem(item);

      if (response && response['result'] && Array.isArray(response['result'])) {
        const dataArray = response['result'];
        mappedData = dataArray.map((item: any) => mapItem(item));
      } else if (response && response['data'] && Array.isArray(response['data'])) {
        const dataArray = response['data'];
        mappedData = dataArray.map((item: any) => mapItem(item));
        if (response['total'] !== undefined) {
          originalTotalCount = response['total'];
        } else if (response['count'] !== undefined) {
          originalTotalCount = response['count'];
        }
      } else if (Array.isArray(response)) {
        mappedData = response.map((item: any) => mapItem(item));
        originalTotalCount = mappedData.length;
      } else if (response && response['paginationInfo']) {
        // Handle case where response has paginationInfo but no result/data array
        // This ensures totalItems is updated even if result array is missing
        if (response['paginationInfo']['totalCount'] !== undefined) {
          originalTotalCount = response['paginationInfo']['totalCount'];
        }
      }
      
      // Filter to only show Dataset, Model, and Use Case asset types (skip for Compute and User action tabs)
      if (this.activeLogType === 'ASSET') {
        // Backend may return either DataBank/AiModel/UseCase/Model OR Dataset (normalized to adex:* by normalizeApiEntityType()).
        // Keep the allow-list broad enough so valid asset activities aren't filtered out.
        const allowedAssetTypes = [
          'adex:DataBank',
          'adex:Dataset',
          'adex:AiModel',
          'adex:Model',
          'adex:UseCase',
        ];
        mappedData = mappedData.filter((item: ActivityData) => {
          const assetTypeApiValue = item.assetTypeApiValue || '';
          // If backend didn't send a type, don't drop the row.
          if (!assetTypeApiValue) return true;
          return allowedAssetTypes.some(allowedType => {
            const normalizedApiValue = assetTypeApiValue.toLowerCase();
            const normalizedAllowedType = allowedType.toLowerCase();
            return normalizedApiValue === normalizedAllowedType;
          });
        });
      }
      
      // Update pagination totalItems from API response
      // Use originalTotalCount from API since we're filtering on the server side with entityType parameters
      if (originalTotalCount !== undefined) {
        this.pagination = { 
          ...this.pagination, 
          totalItems: originalTotalCount 
        };
      } else {
        // Fallback: if no paginationInfo, use filtered count (shouldn't happen in normal flow)
        this.pagination = { 
          ...this.pagination, 
          totalItems: mappedData.length 
        };
      }
      
      this.activitiesData = mappedData;
      this.allActivitiesData = [...mappedData];
      
      // Dynamically generate filter options from actual data
      this.updateFilterOptions(mappedData);
      
      // Update filter fields with new options, merging with defaults
      const typeField = this.filterFields.find(f => f.key === 'type');
      if (typeField) {
        // Merge default options with dynamically found options, removing duplicates
        const defaultOptions = this.filterEnumsService.getUserActivitiesAssetTypeOptions();
        const allOptions = [...new Set([...defaultOptions, ...this.typeOptions])];
        typeField.options = allOptions.sort();
      }
      const activityField = this.filterFields.find(f => f.key === 'activity');
      if (activityField) {
        // Merge default activities with dynamically found activities, removing duplicates
        // Use tab-specific options for Compute and User action
        const defaultActivities = this.activeLogType === 'COMPUTE'
          ? this.computeActivityOptions
          : this.activeLogType === 'USER_ACTION'
            ? this.userActionActivityOptions
            : this.filterEnumsService.getActivityOptions();
        const allActivities = [...new Set([...defaultActivities, ...this.activityOptions])];
        activityField.options = allActivities.sort();
      }
      
      if (mappedData.length === 0) {
        this.emptyMessage = 'No User Activities Found';
      }
    } catch (error) {
      console.error('Error loading user activities:', error);
      this.activitiesData = [];
      this.allActivitiesData = [];
      this.pagination = { ...this.pagination, totalItems: 0 };
      this.emptyMessage = 'No user activities found';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Mapping of known uppercase entity types to their camelCase equivalents
   * This handles cases where API returns all-uppercase values
   */
  private entityTypeMapping: Record<string, string> = {
    'DATABANK': 'DataBank',
    'AIMODEL': 'AiModel',
    'APPS': 'Apps',
    'MODEL': 'Model',
    'DATASET': 'Dataset',
    'USECASE': 'UseCase',
    'CHALLENGE': 'Challenge',
  };

  /**
   * Normalize API entity type value to standard format: "adex:DataBank"
   * Handles various input formats: "DATABANK", "adex:DATABANK", "adex:DataBank", etc.
   */
  normalizeApiEntityType(value: string): string {
    if (!value) return '';
    
    // Remove existing "adex:" prefix if present (case insensitive)
    let cleaned = value.replace(/^adex:/i, '').trim();
    
    if (!cleaned) return '';
    
    // Convert to proper casing: DATABANK -> DataBank, DATA_BANK -> DataBank
    let normalized = '';
    
    if (cleaned.includes('_')) {
      // Handle SNAKE_CASE: DATA_BANK -> DataBank
      normalized = cleaned
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join('');
    } else if (cleaned === cleaned.toUpperCase() && cleaned.length > 1) {
      // All uppercase (e.g., "DATABANK"): check mapping table first
      if (this.entityTypeMapping[cleaned]) {
        normalized = this.entityTypeMapping[cleaned];
      } else {
        // If not in mapping, try to convert: DATABANK -> DataBank
        // For now, just capitalize first letter and lowercase rest
        // This is a fallback - the mapping table should cover most cases
        normalized = cleaned.charAt(0) + cleaned.slice(1).toLowerCase();
      }
    } else {
      // Already in camelCase/PascalCase or mixed case
      // Just ensure first letter is capitalized
      normalized = cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
    }
    
    return `adex:${normalized}`;
  }

  toPascalCase(str: string): string {
    if (!str) return '';
    
    // Remove "adex:" prefix if present for display purposes
    let cleaned = str.replace(/^adex:/i, '');
    
    // Convert camelCase/PascalCase to space-separated words
    // e.g., "DataBank" -> "Data Bank", "AiModel" -> "Ai Model"
    cleaned = cleaned.replace(/([a-z])([A-Z])/g, '$1 $2');
    
    // Convert SNAKE_CASE or UPPERCASE to PascalCase
    return cleaned
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Map display asset type name to API format
   * Examples: "Databank" -> "adex:DataBank", "Ai Model" -> "adex:AiModel", "Use Case" -> "adex:UseCase"
   * Converts PascalCase display names to API format by removing spaces and prefixing with "adex:"
   */
  mapAssetTypeToApiFormat(displayName: string): string {
    if (!displayName) return '';
    
    // Split by spaces, capitalize first letter of each word, join without spaces
    // Examples: "Databank" -> "DataBank", "Ai Model" -> "AiModel", "Use Case" -> "UseCase"
    const apiValue = displayName
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join('');
    
    return `adex:${apiValue}`;
  }

  updateFilterOptions(data: ActivityData[]): void {
    // Get unique asset types from data (using display names)
    // Filter out excluded types using the service
    const uniqueTypes = [...new Set(data.map(item => item.assetType).filter(Boolean))]
      .filter(type => !this.filterEnumsService.isExcludedAssetType(type));
    
    // Merge default options with dynamic options, ensuring uniqueness and sorting
    const defaultOptions = this.filterEnumsService.getUserActivitiesAssetTypeOptions();
    const mergedTypes = [...new Set([...defaultOptions, ...uniqueTypes])];
    this.typeOptions = [...mergedTypes.sort()];
    
    // Build a map of display names to API values for filtering
    this.assetTypeDisplayToApiMap = new Map<string, string>();
    data.forEach(item => {
      if (item.assetType && item.assetTypeApiValue && !this.filterEnumsService.isExcludedAssetType(item.assetType)) {
        this.assetTypeDisplayToApiMap.set(item.assetType, item.assetTypeApiValue);
      }
    });

    // Get unique activities from data
    const uniqueActivities = [...new Set(data.map(item => item.activity).filter(Boolean))];
    
    // Merge default activities with dynamic activities, ensuring uniqueness and sorting
    // Use tab-specific options for Compute and User action
    const defaultActivities = this.activeLogType === 'COMPUTE'
      ? this.computeActivityOptions
      : this.activeLogType === 'USER_ACTION'
        ? this.userActionActivityOptions
        : this.filterEnumsService.getActivityOptions();
    const mergedActivities = [...new Set([...defaultActivities, ...uniqueActivities])];
    this.activityOptions = [...mergedActivities.sort()];
  }

  onPageChange(page: number): void {
    this.pagination = { ...this.pagination, currentPage: page };
    this.loadActivities();
  }

  onPageSizeChange(pageSize: number): void {
    this.pagination = { ...this.pagination, pageSize: pageSize, currentPage: 1 };
    this.loadActivities();
  }

  onSortChange(event: { column: string; direction: 'asc' | 'desc' }): void {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }

  onFilterChange(filterValues: Record<string, any>): void {
    this.filters = {
      type: filterValues['type'] || [],
      activity: filterValues['activity'] || [],
      dateRange: filterValues['dateRange'] || null
    };
    // Reset to first page when filters change
    this.pagination = { ...this.pagination, currentPage: 1 };
    // Reload activities with new filters (server-side filtering)
    this.loadActivities();
  }

  onClearFilters(): void {
    this.filters = { type: [], activity: [], dateRange: null };
    // Reset filter field values
    this.filterFields.forEach(field => {
      if (field.type === 'multiselect') {
        field.value = [];
      } else if (field.type === 'dateRange') {
        field.value = null;
      } else {
        field.value = '';
      }
    });
    this.pagination = { ...this.pagination, currentPage: 1 };
    this.loadActivities();
  }

  /**
   * Get activity class for styling based on activity type
   */
  getActivityClass(activity: string): string {
    const activityLower = activity?.toLowerCase() || '';
    
    if (activityLower.includes('view') || activityLower === 'view') {
      return 'activity-view';
    } else if (activityLower.includes('create') || activityLower === 'create') {
      return 'activity-create';
    } else if (activityLower.includes('update') || activityLower === 'update') {
      return 'activity-update';
    } else if (activityLower.includes('delete') || activityLower === 'delete') {
      return 'activity-delete';
    } else if (activityLower.includes('download') || activityLower === 'download') {
      return 'activity-download';
    } else if (activityLower.includes('submit') || activityLower === 'submit') {
      return 'activity-submit';
    } else if (activityLower.includes('approve') || activityLower === 'approve') {
      return 'activity-approve';
    } else if (activityLower.includes('reject') || activityLower === 'reject') {
      return 'activity-reject';
    } else if (activityLower.includes('like') || activityLower === 'like') {
      return 'activity-like';
    } else if (activityLower.includes('bookmark') || activityLower === 'bookmark') {
      return 'activity-bookmark';
    } else if (activityLower.includes('dislike') || activityLower === 'dislike') {
      return 'activity-dislike';
    }
    
    return 'activity-default';
  }

  ngAfterViewInit(): void {
    // Assign templates synchronously to avoid race condition with data loading
    // Assign activity cell template to the activity column (Asset / User Actions tabs)
    const activityColumn = this.assetColumns.find(col => col.key === 'activity');
    if (activityColumn && this.activityCellTemplate) {
      activityColumn.cellTemplate = this.activityCellTemplate;
    }

    // Assign action cell template to the action column (Asset / User Actions only; Compute tab has no dropdown)
    const assetActionColumn = this.assetColumns.find(col => col.key === 'action');
    if (assetActionColumn && this.actionCellTemplate) {
      assetActionColumn.cellTemplate = this.actionCellTemplate;
    }
    // Compute tab: use activity cell template for the Action column (shows API action value with chip styling)
    const computeActivityColumn = this.computeColumns.find(col => col.key === 'activity');
    if (computeActivityColumn && this.activityCellTemplate) {
      computeActivityColumn.cellTemplate = this.activityCellTemplate;
    }

    // Initialize modal tabs
    this.initializeModalTabs();
  }

  initializeModalTabs(): void {
    if (this.userInfoTabContentTemplate && this.activityInfoTabContentTemplate) {
      this.modalTabs = [
        {
          key: 'activityInfo',
          label: 'Activity Information',
          contentTemplate: this.activityInfoTabContentTemplate
        },
        {
          key: 'userInfo',
          label: 'User',
          contentTemplate: this.userInfoTabContentTemplate
        }
      ];
    }
  }

  toggleActionDropdown(event: Event, row: ActivityData): void {
    event.stopPropagation();
    const rowId = row.id || JSON.stringify(row);
    if (this.openDropdownId === rowId) {
      this.openDropdownId = null;
      this.dropdownPosition = null;
      return;
    }
    const btn = event.currentTarget as HTMLElement;
    const rect = btn.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width
    };
    this.openDropdownId = rowId;
  }

  closeActionDropdown(): void {
    this.openDropdownId = null;
    this.dropdownPosition = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openDropdownId !== null &&
        !target.closest('.action-cell-wrapper') &&
        !target.closest('.action-dropdown-overlay')) {
      this.closeActionDropdown();
    }
  }

  async handleAction(action: string, row: ActivityData): Promise<void> {
    if (action === 'view') {
      try {
        // Call the API endpoint for user details
        const userId = row.userId || '76a30787-e524-4a16-aae6-d6c87f7f97c9'; // Use row userId or fallback to provided ID
        
        // Make API call using AuthApiService
        const userDetailsResponse = await this.authApiService.getUserInfoById(userId);
        
        // Store the response and show modal
        // The API response has the user data nested in a 'result' property
        this.selectedActivity = row;
        this.userDetails = userDetailsResponse?.['result'] || userDetailsResponse; // Extract result if present, otherwise use response directly
        
        // Reinitialize tabs with updated templates
        this.initializeModalTabs();
        
        this.isModalVisible = true;
        
        // Log the response (you can use this data in the modal if needed)
        console.log('User details:', this.userDetails);
      } catch (error) {
        console.error('Error fetching user details:', error);
        // Still show the modal with available data even if API call fails
        this.selectedActivity = row;
        this.userDetails = null; // Clear user details on error
        this.isModalVisible = true;
      }
    }
    // Implement other action logic here (edit, delete, etc.)
    this.closeActionDropdown();
  }

  closeModal(): void {
    this.isModalVisible = false;
    this.selectedActivity = null;
    this.userDetails = null;
  }

  onModalClose(): void {
    this.closeModal();
  }

  isDropdownOpen(row: ActivityData): boolean {
    const rowId = row.id || JSON.stringify(row);
    return this.openDropdownId === rowId;
  }

  /** Index in activitiesData of the row whose action dropdown is open; null when none. */
  get openDropdownRowIndex(): number | null {
    if (this.openDropdownId == null) return null;
    const idx = this.activitiesData.findIndex(
      (r) => (r?.id ?? JSON.stringify(r)) === this.openDropdownId
    );
    return idx >= 0 ? idx : null;
  }

  /** Row whose action dropdown is open; used by the fixed overlay. */
  get openDropdownRow(): ActivityData | null {
    if (this.openDropdownId == null) return null;
    return this.activitiesData.find(
      (r) => (r?.id ?? JSON.stringify(r)) === this.openDropdownId
    ) ?? null;
  }

  /** Position for the fixed dropdown overlay (so it appears on top of the table). */
  dropdownPosition: { top: number; left: number; width: number } | null = null;
}
